require('./bootstrap');
import { createApp } from 'vue';
import { createPinia } from 'pinia';
import router from './router';
import App from './App.vue';

import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import mitt from 'mitt';
import moment from "moment";
import { Field, Form, ErrorMessage, defineRule, configure } from 'vee-validate';
import { all } from '@vee-validate/rules';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import Vue3EasyDataTable from 'vue3-easy-data-table';
import 'vue3-easy-data-table/dist/style.css';
import '@imengyu/vue3-context-menu/lib/vue3-context-menu.css';
import ContextMenu from '@imengyu/vue3-context-menu';
import Toast from 'vue-toastification';
import 'vue-toastification/dist/index.css';

const app = createApp(App);
const pinia = createPinia();
const emitter = mitt();

app.use(pinia);
app.use(router);
app.use(ContextMenu);
app.use(Toast, { position: 'top-right', timeout: 5000 });

// Provide for Composition API inject
app.provide('emitter', emitter);
app.provide('s3RootUrl', window.s3RootUrl || '');

// Global properties
app.config.globalProperties.$emitter = emitter;
app.config.globalProperties.$baseUrl = window.baseUrl;
app.config.globalProperties.$s3RootUrl = window.s3RootUrl;

app.config.globalProperties.$notification = (type, message) => {
    $.toast({
        heading: type.toUpperCase(),
        text: message,
        icon: type,
        showHideTransition: 'slide',
        position: 'top-right',
        hideAfter: 5000,
        stack: false
    })
};

app.config.globalProperties.$truncateString = (str, maxLength) => {
    if (str && str.length > maxLength) {
        return str.substring(0, maxLength) + '...';
    }
    return str;
};

app.config.globalProperties.$moment = (date, format = null) => {
    if (format) {
        return moment(date, format);
    }
    return moment(date);
};

app.config.globalProperties.$dateTimeFormat = (date, format) => {
    return moment(date).format(format);
};

app.config.globalProperties.$capitalize = (str) => {
    return str.replace(/\w\S*/g, function (txt) {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
};

app.config.globalProperties.$cardRandomBgClass = () => {
    let cardItemClasses = [
        "task-bg-green",
        "task-bg-light-green",
        "task-bg-purple",
        "task-bg-orange",
        "task-bg-warning",
        "task-bg-blue",
        "task-bg-default",
        "task-bg-pink",
    ];
    return cardItemClasses[Math.floor(Math.random() * cardItemClasses.length)];
};

// Check user role/permissions - assuming window.userRoles/Permissions are set in blade
app.config.globalProperties.$hasRole = (role) => {
    return window.userRoles && window.userRoles.includes(role);
};

app.config.globalProperties.$hasAnyRole = (roles) => {
    return roles.some(role => window.userRoles && window.userRoles.includes(role));
};

app.config.globalProperties.$hasPermission = (permission) => {
    if (window.userRoles && window.userRoles.includes('provider')) {
        return true;
    }
    return window.userPermissions && window.userPermissions.includes(permission);
};

app.config.globalProperties.$hasAnyPermission = (permissions) => {
    if (window.userRoles && window.userRoles.includes('provider')) {
        return true;
    }
    return permissions.some(permission => window.userPermissions && window.userPermissions.includes(permission));
};

app.config.globalProperties.$goToPage = (url, newTab = false) => {
    if (newTab) {
        window.open(url, '_blank');
        return;
    }
    else {
        // Use router if possible, else window
        if (url.startsWith('/')) {
            router.push(url);
        } else {
            window.location.href = url;
        }
    }
};

// Directives
import { tooltipDirective } from './directives/tooltip';
import { popoverDirective } from './directives/popover';
app.directive('tooltip', tooltipDirective);
app.directive('popover', popoverDirective);

// Components
app.component('VueDatePicker', VueDatePicker);
app.component('Form', Form);
app.component('Field', Field);
app.component('ErrorMessage', ErrorMessage);
app.component('vue-multiselect', Multiselect);
app.component('Vue3EasyDataTable', Vue3EasyDataTable);

// Vee-validate rules
Object.entries(all).forEach(([name, rule]) => {
    defineRule(name, rule);
});

configure({
    validateOnInput: true,
    generateMessage: (context) => {
        const messages = {
            required: 'This field is required',
            email: 'Please enter a valid email address',
            url: 'Please enter a valid URL',
            min: `The field must be at least ${context.rule.params[0]} characters`,
            numeric: `The field ${(context.field).replace(/_/g, ' ')} must be a number.`,
            min_value: `The field ${(context.field).replace(/_/g, ' ')} must be at least ${context.rule.params[0]}.`,
        };
        return messages[context.rule.name] || `The field ${(context.field).replace(/_/g, ' ')} is invalid`;
    },
});

app.mount('#app');
